// api.ts
import { ApiEndpoints } from "@shared/api/api-endpoints";
import { axApiClient, wxApiClient } from "@shared/api/clients/apiClientFactory";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import {
  AssetListResponse,
  CancelServiceOrderRequest,
  CreateServiceOrderRequest,
  CreateServiceOrderResponse,
  FacilityListResponse,
  JobCardData,
  NetworkListResponse,
  ServiceOrderHistoryListResponse,
  ServiceOrderHistorySummaryResponse,
  ServiceOrdersListResponse,
  ServiceOrderSummaryResponse,
  SopListResponse,
  SystemListResponse,
} from "./types";

export const axServiceOrderRegistry = {
  getServiceOrders: async (
    remoteUtilityId: string,
    page?: number,
    limit?: number,
    searchData?: string,
    status?: string,
    priority?: string,
    sopAssociationType?: string,
    fieldForceName?: string
  ): Promise<ServiceOrdersListResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "service-order",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("reading_source", "AX");
        query.push("is_pagination_required", 1);
        query.push("page", page);
        query.push("limit", limit);

        if (searchData) query.push("search_data", searchData);
        if (status) query.push("status", status);
        if (priority) query.push("priority", priority);
        if (sopAssociationType)
          query.push("sop_association_type", sopAssociationType);
        if (fieldForceName) query.push("field_force_name", fieldForceName);
      }
    );

    const response = await axApiClient.get(url);
    return response.data;
  },

  getServiceOrderDetails: async (
    remoteUtilityId: string,
    serviceOrderId: string
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `service-order/detail/${serviceOrderId}`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      }
    );

    const response = await axApiClient.get(url);
    return response.data;
  },

  getServiceOrderSummary: async (
    remoteUtilityId: string
  ): Promise<ServiceOrderSummaryResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "service-order-summary",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      }
    );

    const response = await axApiClient.get(url);
    return response.data.result;
  },
  // Add to your existing axServiceOrderRegistry object
  getServiceOrderHistorySummary: async (
    remoteUtilityId: string
  ): Promise<ServiceOrderHistorySummaryResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "service-order-history-summary",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      }
    );

    const response = await axApiClient.get(url);
    return response.data;
  },
  getSOPList: async (
    remoteUtilityId: string,
    page?: number,
    limit?: number,
    searchData?: string,
    sopAssociationType?: string,
    operationType?: string
  ): Promise<SopListResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "service-order-template",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("reading_source", "AX");
        query.push("page", page);
        query.push("limit", limit);

        if (searchData) query.push("search_data", searchData);
        if (sopAssociationType)
          query.push("association_type", sopAssociationType);
        if (operationType) query.push("operation_type", operationType);
      }
    );

    const response = await axApiClient.get(url);
    return response.data;
  },

  getServiceOrderHistory: async (
    remoteUtilityId: string,
    page?: number,
    limit?: number,
    searchData?: string,
    status?: string,
    priority?: string,
    orderType?: string,
    assignedTo?: string
  ): Promise<ServiceOrderHistoryListResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "service-order",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("is_history", "1");
        query.push("page", (page || 1).toString());
        query.push("limit", (limit || 25).toString());

        if (searchData) query.push("search_data", searchData);
        if (status) query.push("status", status);
        if (priority) query.push("priority", priority);
        if (orderType) query.push("operation_type", orderType);
        if (assignedTo) query.push("field_force_name", assignedTo);
        query.push("is_pagination_required", 1);
      }
    );

    const response = await axApiClient.get(url);
    return response.data;
  },

  //
  getServiceOrderAssets: async (
    remoteUtilityId: string,
    page?: number,
    limit?: number,
    searchData?: string,
    type?: string,
    area?: string,
    system?: string,
    network?: string,
    facility?: string
  ): Promise<AssetListResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "assets",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("page", (page || 1).toString());
        query.push("limit", (limit || 50).toString());

        if (searchData) query.push("search", searchData);
        if (type) query.push("asset_type_code", type);
        if (area) query.push("area", area);
        if (system) query.push("system", system);
        if (network) query.push("network", network);
        if (facility) query.push("facility", facility);
      }
    );

    const response = await axApiClient.get(url);
    return response.data;
  },

  // Get Systems for Service Order
  getServiceOrderSystems: async (
    remoteUtilityId: string,
    page?: number,
    limit?: number,
    searchData?: string,
    type?: string,
    area?: string,
    facility?: string
  ): Promise<SystemListResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "systems",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("page", (page || 1).toString());
        query.push("limit", (limit || 50).toString());
        query.push("is_pagination_required", 1);
        if (searchData) query.push("search", searchData);
        if (type) query.push("system_type", type);
        if (area) query.push("area", area);
        if (facility !== undefined && facility !== null && facility !== "") {
          query.push("facility", facility);
        }
      }
    );

    const response = await axApiClient.get(url);
    return response.data;
  },

  // Get Facilities for Service Order
  getServiceOrderFacilities: async (
    remoteUtilityId: string,
    page?: number,
    limit?: number,
    searchData?: string,
    type?: string,
    area?: string,
    facility?: string
  ): Promise<FacilityListResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "facilities",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("page", (page || 1).toString());
        query.push("limit", (limit || 50).toString());

        if (searchData) query.push("search", searchData);
        if (type !== undefined && type !== null && type !== "") {
          query.push("facility_type", type);
        }
        if (area) query.push("area", area);
      }
    );

    const response = await axApiClient.get(url);
    return response.data;
  },

  // Get Networks for Service Order
  getServiceOrderNetworks: async (
    remoteUtilityId: string,
    page?: number,
    limit?: number,
    searchData?: string,
    type?: string,
    area?: string,
    facility?: string
  ): Promise<NetworkListResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "networks",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("page", (page || 1).toString());
        query.push("limit", (limit || 50).toString());
        query.push("is_pagination_required", "1");
        if (searchData) query.push("search", searchData);
        if (type !== undefined && type !== null) {
          query.push("type", type);
        }
        if (area) query.push("area", area);
        if (facility) query.push("facility_id", facility);
      }
    );

    const response = await axApiClient.get(url);
    return response.data;
  },

  createServiceOrder: async (
    remoteUtilityId: string,
    jobCardData: JobCardData[]
  ): Promise<CreateServiceOrderResponse> => {
    const url = ApiEndpoints.createUrl("ax", "service-order");

    const payload: CreateServiceOrderRequest = {
      remoteUtilityId: parseInt(remoteUtilityId),
      jobCardData: jobCardData,
    };

    const response = await axApiClient.post(url, payload);
    return response.data;
  },

  updateServiceOrder: async (
    remoteUtilityId: string,
    id: number,
    jobCardData: JobCardData[]
  ): Promise<CreateServiceOrderResponse> => {
    const url = ApiEndpoints.createUrl("ax", `service-order/${id}`);

    const payload: CreateServiceOrderRequest = {
      remoteUtilityId: parseInt(remoteUtilityId),
      jobCardData: jobCardData,
    };

    const response = await axApiClient.put(url, payload);
    return response.data;
  },

  cancelServiceOrders: async (
    remoteUtilityId: string,
    jobCardIds: number[]
  ): Promise<any> => {
    const payload: CancelServiceOrderRequest = {
      jobCardId: jobCardIds,
      remoteUtilityId: parseInt(remoteUtilityId),
    };

    const response = await axApiClient.patch("service-order-cancel/", payload);
    return response.data;
  },

  getServiceOrderDetail: async (id: string): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `service-order/detail/${id}`,
      (query) => {
        query.push("remote_utility_id", getRemoteUtilityId());
      }
    );

    const response = await axApiClient.get(url);
    return response.data.result;
  },
  getAllOperationType: async (): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `choices/operation_type`
    );

    const response = await axApiClient.get(url);
    return response.data.result;
  },

  downloadReport: async (
    reportId: number,
    remoteUtilityId: string,
    tabName?: string
  ): Promise<Blob> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "wx",
      `download-report/${reportId}`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        if (tabName != "") {
          query.push("tab_name", tabName);
        }
      }
    );
    const response = await wxApiClient.get(url, {
      responseType: "blob",
    });
    return response.data;
  },
};
